import type { OrderablePackage, ShippingChannel } from './orders.js';

/**
 * Search, sort and filter for the two long lists in the create/edit-order flow (MYP-48 follow-up).
 *
 * Step 1 offers every orderable package on the account — over a hundred on the test account — and
 * Step 2 every channel `channels` returns, 205 of them. Shown whole, the one you want is a long
 * scroll away. These functions only decide *which* rows are shown and in what order; paging is
 * `packagePageCount`/`clampPackagePage` from `packages.ts`, which already take a page size.
 *
 * Nothing here changes what is selected. A package filtered out of view stays ticked, and the
 * selected channel stays selected when it is paged away — the screens show both in a summary line.
 */

/** Rows per page on Step 1. */
export const ORDER_PACKAGES_PER_PAGE = 10;

/** Cards per page on Step 2 — 12 fills whole rows of 4, 3, 2 or 1 cards (MYP-131). */
export const ORDER_CHANNELS_PER_PAGE = 12;

/** Identifiers are compared with separators stripped: `YT-4834 6981` finds `YT48346981`. */
function normaliseIdentifier(value: string): string {
  return value.toLowerCase().replace(/[\s\-_]/g, '');
}

function includesText(haystack: string | undefined, needle: string): boolean {
  return haystack !== undefined && haystack.toLowerCase().includes(needle);
}

/* -------------------------------------------------------------------------------------------- *
 * Step 1 — packages
 * -------------------------------------------------------------------------------------------- */

/**
 * `default` keeps the order the list arrived in: newest first from `package_list`, with an edited
 * order's own packages ahead of the rest. There is deliberately no "selected first" order — rows
 * would jump under the pointer the moment one is ticked. `show: 'selected'` answers that instead.
 */
export type PackagePickSort = 'default' | 'tracking' | 'heaviest';

export const PACKAGE_PICK_SORTS: readonly PackagePickSort[] = ['default', 'tracking', 'heaviest'];

export interface PackagePickFilters {
  readonly search: string;
  readonly sort: PackagePickSort;
  /** `selected` narrows to what is ticked — the way to review a selection spread over pages. */
  readonly show: 'all' | 'selected';
}

export const EMPTY_PACKAGE_PICK_FILTERS: PackagePickFilters = {
  search: '',
  sort: 'default',
  show: 'all',
};

export function hasActivePackagePickFilters(filters: PackagePickFilters): boolean {
  return filters.search.trim() !== '' || filters.sort !== 'default' || filters.show !== 'all';
}

/**
 * Whether a package answers to the search box: its tracking number (separators ignored) or its
 * declared item name (literally, case-insensitive).
 */
export function matchesPackagePick(item: OrderablePackage, query: string): boolean {
  const trimmed = query.trim();
  if (trimmed === '') return true;

  const identifier = normaliseIdentifier(trimmed);
  return (
    (identifier !== '' && normaliseIdentifier(item.trackingNumber).includes(identifier)) ||
    includesText(item.itemName, trimmed.toLowerCase())
  );
}

/**
 * The packages to list, in the order to list them.
 *
 * `heaviest` puts parcels the warehouse has not weighed yet last rather than treating them as
 * 0 kg, which would read as "the lightest" when the truth is "unknown". Ties keep their original
 * order (the sort is stable), so paging never shuffles rows of equal weight.
 */
export function filterPackagePicks(
  pool: readonly OrderablePackage[],
  selectedIds: readonly string[],
  filters: PackagePickFilters,
): readonly OrderablePackage[] {
  const shown = pool.filter(
    (item) =>
      (filters.show === 'all' || selectedIds.includes(item.id)) &&
      matchesPackagePick(item, filters.search),
  );

  if (filters.sort === 'tracking') {
    return [...shown].sort((a, b) =>
      a.trackingNumber.localeCompare(b.trackingNumber, undefined, { numeric: true }),
    );
  }

  if (filters.sort === 'heaviest') {
    return [...shown].sort((a, b) => {
      if (a.weightGrams === undefined) return b.weightGrams === undefined ? 0 : 1;
      if (b.weightGrams === undefined) return -1;
      return b.weightGrams - a.weightGrams;
    });
  }

  return shown;
}

/* -------------------------------------------------------------------------------------------- *
 * Step 2 — channels
 * -------------------------------------------------------------------------------------------- */

/**
 * `default` is the vendor's own order. `fastest` sorts on the shortest quoted transit, then the
 * longest; a channel quoting 0–0 days has no transit on record (`transday1`/`transday2` empty) and
 * goes last rather than first.
 */
export type ChannelPickSort = 'default' | 'fastest' | 'name';

export const CHANNEL_PICK_SORTS: readonly ChannelPickSort[] = ['default', 'fastest', 'name'];

export interface ChannelPickFilters {
  readonly search: string;
  /** A transport-type code (AIR, SEA, …) or `all`. */
  readonly transport: string;
  readonly sort: ChannelPickSort;
}

export const EMPTY_CHANNEL_PICK_FILTERS: ChannelPickFilters = {
  search: '',
  transport: 'all',
  sort: 'default',
};

export function hasActiveChannelPickFilters(filters: ChannelPickFilters): boolean {
  return filters.search.trim() !== '' || filters.transport !== 'all' || filters.sort !== 'default';
}

/** The transport types the channels actually use, in the order they first appear. */
export function channelTransportTypes(channels: readonly ShippingChannel[]): readonly string[] {
  const types: string[] = [];
  for (const channel of channels) {
    if (channel.transportType !== '' && !types.includes(channel.transportType)) {
      types.push(channel.transportType);
    }
  }
  return types;
}

/** Whether a channel answers to the search box: either of its names, case-insensitive. */
export function matchesChannelPick(channel: ShippingChannel, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return includesText(channel.name, needle) || includesText(channel.nameNative, needle);
}

function hasTransit(channel: ShippingChannel): boolean {
  return channel.transitDaysMin > 0 || channel.transitDaysMax > 0;
}

export function filterChannelPicks(
  channels: readonly ShippingChannel[],
  filters: ChannelPickFilters,
): readonly ShippingChannel[] {
  const shown = channels.filter(
    (channel) =>
      (filters.transport === 'all' || channel.transportType === filters.transport) &&
      matchesChannelPick(channel, filters.search),
  );

  if (filters.sort === 'name') {
    return [...shown].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }

  if (filters.sort === 'fastest') {
    return [...shown].sort((a, b) => {
      const knownA = hasTransit(a);
      const knownB = hasTransit(b);
      if (knownA !== knownB) return knownA ? -1 : 1;
      return a.transitDaysMin - b.transitDaysMin || a.transitDaysMax - b.transitDaysMax;
    });
  }

  return shown;
}

/** The slice of `items` on `page` (1-based). The caller clamps `page` first. */
export function pageOf<T>(items: readonly T[], page: number, perPage: number): readonly T[] {
  const start = (page - 1) * perPage;
  return items.slice(start, start + perPage);
}
